import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {setUpStore, callTargetedAction} from 'client/framework';
import searchDropdownActions from 'sg/Dropdowns/SearchDropdown/SearchDropdown.actions';
import SearchDropdown from 'sg/Dropdowns/SearchDropdown/SearchDropdown.react';
import SearchDropdownStore from 'sg/Dropdowns/SearchDropdown/SearchDropdown.store';
import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';
import sessionStore from 'client/Session/SessionStore';
import {TagModelV1} from 'resources/GeneratedModels/Tag/TagModel.v1';
import {CheckboxChip, Button, Text} from '@albert-io/atomic';

import './question-tag-editor.scss';

export default class QuestionTagEditor extends React.Component {
  static propTypes = {
    tags: ImmutablePropTypes.list,
    onTagClick: PropTypes.func
  };

  getStore = () => {
    return setUpStore(SearchDropdownStore, 'QuestionTagEditorSearchStore');
  };

  getQueries() {
    const searchString = this.getStore().getSearchString();
    return {
      exactMatchQuery: mandarkEndpoint(['tags_v1']).filter({
        name: {
          exact: searchString
        }
      }),
      searchQuery: mandarkEndpoint(['tags_v1'])
        .filter({
          name: {
            case_insensitive_substring: searchString
          }
        })
        // Given that there are thousands of tags, and the fact that this endpoint doesn't
        // allow page_size: 'infinite' we're going to limit the number of results to a high number
        .pageSize(500)
        .sort('name')
    };
  }

  areQueriesReady() {
    return this.getStore().getSearchString()
      ? Object.values(this.getQueries()).every((query) => query.isResourcePopulated())
      : true;
  }

  getOptions() {
    const searchString = this.getStore().getSearchString();
    if (!searchString || !this.areQueriesReady()) {
      return null;
    }
    const {searchQuery, exactMatchQuery} = this.getQueries();
    const searchResults = searchQuery.getResource();
    return exactMatchQuery
      .getResource()
      .concat(searchResults.filterNot((tag) => tag.getName() === searchString));
  }

  areResultsLoading() {
    return this.getStore().getSearchString() !== '' && !this.areQueriesReady();
  }

  getTagsList() {
    return this.props.tags.map((tag, key) => (
      <CheckboxChip
        defaultChecked
        icon='tag'
        key={key}
        onChange={(e) => this.handleTagClick(tag, !e.target.checked)}
      >
        {tag.getName()}
      </CheckboxChip>
    ));
  }

  handleTagClick = (...args) => {
    this.props.onTagClick(...args);
    callTargetedAction({
      name: searchDropdownActions.RESET_STORE,
      targetStore: this.getStore().getName()
    });
  };

  handleCreateNewTag = async () => {
    const tag = await new TagModelV1({
      name: this.getStore().getSearchString()
    }).save();

    this.handleTagClick(tag);
  };

  isCreatingNewTag() {
    return (
      this.areQueriesReady() &&
      this.getQueries()
        .exactMatchQuery.getResource()
        .isEmpty()
    );
  }

  render() {
    const createTagButton = (
      <Button size='s' onClick={this.handleCreateNewTag} disabled={!this.isCreatingNewTag()}>
        Create new tag: {this.getStore().getSearchString()}
      </Button>
    );

    const emptyResultsComponent = sessionStore.isSuper() ? (
      createTagButton
    ) : (
      <Text>No results found</Text>
    );
    return (
      <div className='question-tag-editor'>
        <SearchDropdown
          label='Tags'
          storeName={this.getStore().getName()}
          options={this.getOptions()}
          displayName='name'
          handleOptionChange={this.handleTagClick}
          areResultsLoading={this.areResultsLoading()}
          placeholder='Search by name'
          bottomMessage={sessionStore.isSuper() && createTagButton}
          emptyResultsComponent={emptyResultsComponent}
        />
        <div className='question-tag-editor__tag-list'>{this.getTagsList()}</div>
      </div>
    );
  }
}
